/**
 * SupplierSpendChart — two render modes for showing 12 months of supplier spend.
 *
 *   mode="sparkline" — tiny inline area chart, fits in a table cell. No axes
 *                     or labels; just the trend line. Used on the Suppliers list.
 *   mode="bars"      — full bar chart with month labels and £ tooltip.
 *                     Used on the SupplierDetail page header.
 *
 * Always expects `data` shaped as [{ month: 'YYYY-MM', amount: number }] × 12,
 * chronological (oldest first). Renders nothing if all amounts are zero in
 * sparkline mode (avoids a flat line that looks like a bug); the bars mode
 * still renders so admins can see "no spend yet" explicitly.
 */
import React from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const fmtMoney = (v) => `£${(Number(v) || 0).toLocaleString('en-GB', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const fmtMonth = (m) => {
  if (!m) return '';
  const [, mm] = m.split('-');
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return monthNames[parseInt(mm, 10) - 1] || m;
};

export const SupplierSparkline = ({ data, color = '#2563eb' }) => {
  const total = (data || []).reduce((s, d) => s + (d.amount || 0), 0);
  if (total <= 0) {
    return <span className="text-slate-300 text-xs italic">no history</span>;
  }
  return (
    <div className="w-24 h-8" data-testid="supplier-sparkline">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          <defs>
            <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.4} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="amount"
            stroke={color}
            strokeWidth={1.5}
            fill="url(#sparkGrad)"
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export const SupplierBarChart = ({ data }) => {
  // Highlight the most-recent 3 months (current quarter) so seasonality is
  // immediately visible on the detail page.
  const currentQuarter = (data || []).slice(-3).map(d => d.month);
  return (
    <div className="h-56 w-full" data-testid="supplier-bar-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <XAxis
            dataKey="month"
            tickFormatter={fmtMonth}
            tick={{ fontSize: 11, fill: '#64748b' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v) => v >= 1000 ? `£${(v / 1000).toFixed(1)}k` : `£${v}`}
            tick={{ fontSize: 11, fill: '#64748b' }}
            axisLine={false}
            tickLine={false}
            width={45}
          />
          <Tooltip
            cursor={{ fill: 'rgba(37, 99, 235, 0.06)' }}
            formatter={(v) => [fmtMoney(v), 'Spent']}
            labelFormatter={(m) => fmtMonth(m) + ' ' + (m || '').split('-')[0]}
            contentStyle={{
              fontSize: 12,
              borderRadius: 4,
              border: '1px solid #e2e8f0',
            }}
          />
          <Bar dataKey="amount" radius={[3, 3, 0, 0]}>
            {(data || []).map((d, i) => (
              <Cell
                key={i}
                fill={currentQuarter.includes(d.month) ? '#2563eb' : '#94a3b8'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Quarter-over-quarter comparison strip — the current 3 months vs the prior 3. */
export const QuarterCompare = ({ data }) => {
  const arr = data || [];
  if (arr.length < 6) return null;
  const curQ = arr.slice(-3).reduce((s, d) => s + (d.amount || 0), 0);
  const prevQ = arr.slice(-6, -3).reduce((s, d) => s + (d.amount || 0), 0);
  const delta = curQ - prevQ;
  const pct = prevQ > 0 ? (delta / prevQ) * 100 : (curQ > 0 ? 100 : 0);
  const up = delta >= 0;
  if (curQ === 0 && prevQ === 0) return null;
  return (
    <div className="flex items-baseline gap-2 text-xs" data-testid="qoq-compare">
      <span className="text-slate-500">Last 3 mo:</span>
      <span className="font-medium tabular-nums">{fmtMoney(curQ)}</span>
      <span className={`tabular-nums ${up ? 'text-rose-600' : 'text-emerald-600'}`}>
        {up ? '▲' : '▼'} {Math.abs(pct).toFixed(0)}% vs prior 3 mo ({fmtMoney(prevQ)})
      </span>
    </div>
  );
};
